import { useTheme } from '@mui/material';
import centerOfMass from '@turf/center-of-mass';
import { Feature, GeoJsonProperties, Geometry } from 'geojson';
import { useMemo } from 'react';
import {
  FillLayer,
  Layer,
  LineLayer,
  MapRef,
  Source,
  SymbolLayer,
} from 'react-map-gl';
import { useAppSelector } from '../../../../../app/hooks';
import { useGetBlocksQuery } from '../../../../../services/entities/impl/blfApi/endpoints/blocks';
import { ensureLayerExists } from '../../../../map/mapUtils';
import { EMPTY_FEATURE_COLLECTION } from '../../../../map/utils/constants';
import { convertBlocksToFeatureCollection } from '../../../../map/utils/convertBlocksToFeatureCollection';
import { createFeatureCollection } from '../../../../map/utils/createFeatureCollection';
import {
  MapOnboardLayerEnum,
  selectCurrOnboardBlockID,
  selectShowSurroundingBlocksOnMap,
} from '../../../onboardingSlice';
import { OnboardLayerExistenceDict } from '../hooks/useCheckLayersOnboarding';
import useLayerOrderOnboarding from '../hooks/useLayerOrderOnboarding';
import { onboardBorderLineWidth } from './BlockOutlineOnboardingLayer';

interface Props {
  mapRef: MapRef | null;
  layerExistenceDict: OnboardLayerExistenceDict;
}

const SurroundingBlocksLayer: React.FC<Props> = ({
  mapRef,
  layerExistenceDict,
}) => {
  const theme = useTheme();
  const showSurroundingBlocksOnMap = useAppSelector(
    selectShowSurroundingBlocksOnMap
  );
  const currOnboardBlockID = useAppSelector(selectCurrOnboardBlockID);
  const layerBeforeId = useLayerOrderOnboarding(
    MapOnboardLayerEnum.surroundingBlocks,
    layerExistenceDict
  );

  // Show all blocks besides the current block which is displayed in the block outline onboarding layer (I was filtering by ranch but it seems like showing all blocks is still performant so showing everything for now).
  const { currentData: blocks } = useGetBlocksQuery(undefined, {
    selectFromResult: ({ data }) => {
      return {
        currentData: data?.filter((block) => block.id !== currOnboardBlockID),
      };
    },
  });

  const outlineLayerStyle = useMemo<LineLayer>(
    () => ({
      id: `${MapOnboardLayerEnum.surroundingBlocks}-line`,
      type: 'line',
      paint: {
        'line-color': theme.colors.darkTheme.grey.dark,
        'line-opacity': 1,
        'line-width': onboardBorderLineWidth,
      },
    }),
    []
  );

  const fillLayerStyle = useMemo<FillLayer>(
    () => ({
      id: `${MapOnboardLayerEnum.surroundingBlocks}`,
      type: 'fill',
      paint: {
        'fill-color': theme.colors.common.white,
        'fill-opacity': 0.4,
      },
    }),
    []
  );

  const blockLabelLayerStyle = useMemo<SymbolLayer | null>(() => {
    const blockLabelLayerStyle: SymbolLayer = {
      id: `${MapOnboardLayerEnum.surroundingBlocks}-label`,
      type: 'symbol',
      layout: {
        'text-field': ['format', ['get', 'name'], { 'font-scale': 0.9 }],
        'text-size': 18,
        'text-anchor': 'center',
        'text-justify': 'center',
        'text-font': [
          'DIN Pro Medium',
          'Open Sans Regular',
          'Arial Unicode MS Regular',
        ],
      },
      paint: {
        'text-color': '#ffffff',
        'text-halo-color': '#000000',
        'text-halo-width': 0.5,
        'text-halo-blur': 0.5,
      },
    };

    return blockLabelLayerStyle;
  }, []);

  const blocksFeatureCollection = useMemo(
    () => convertBlocksToFeatureCollection(blocks || []),
    [blocks]
  );

  const blocksLabelsFeatureCollection = useMemo(() => {
    if (!blocks) return EMPTY_FEATURE_COLLECTION;
    const features = blocks
      .filter((block) => block.geoPolygon)
      .map((block) => {
        const blockCenter = centerOfMass(block.geoPolygon);

        const newFeature: Feature<Geometry, GeoJsonProperties> = {
          type: 'Feature',
          properties: {
            id: block.id,
            name: block.name,
          },
          geometry: blockCenter.geometry,
        };
        return newFeature;
      });

    if (features && features.length) {
      return createFeatureCollection(features);
    } else {
      return EMPTY_FEATURE_COLLECTION;
    }
  }, [blocks]);

  return (
    <>
      {showSurroundingBlocksOnMap && (
        <Source
          key={MapOnboardLayerEnum.surroundingBlocks}
          id={MapOnboardLayerEnum.surroundingBlocks}
          type='geojson'
          data={blocksFeatureCollection}
        >
          {fillLayerStyle && (
            <Layer
              key={`${MapOnboardLayerEnum.surroundingBlocks}-line`}
              {...fillLayerStyle}
              beforeId={ensureLayerExists(mapRef, layerBeforeId)}
            />
          )}
          {outlineLayerStyle && (
            <Layer
              key={MapOnboardLayerEnum.surroundingBlocks}
              {...outlineLayerStyle}
              beforeId={ensureLayerExists(mapRef, layerBeforeId)}
            />
          )}
        </Source>
      )}
      {showSurroundingBlocksOnMap && (
        <Source
          key={`${MapOnboardLayerEnum.surroundingBlocks}-label`}
          id={`${MapOnboardLayerEnum.surroundingBlocks}-label`}
          type='geojson'
          data={blocksLabelsFeatureCollection}
        >
          {blockLabelLayerStyle && (
            <Layer
              key={`${MapOnboardLayerEnum.surroundingBlocks}-label`}
              {...blockLabelLayerStyle}
              beforeId={ensureLayerExists(mapRef, layerBeforeId)}
            />
          )}
        </Source>
      )}
    </>
  );
};

export default SurroundingBlocksLayer;
